import { getConfigs } from "../configs/Config";
import {
  fetchAccessToken,
  fetchIdToken,
  fetchRefreshToken,
  decodeToken,
  getIdTokenExpiry,
  storeCredentials,
} from "../utils/Auth";
import { Notification } from "src/types/Redux";

export const SESSION_EXPIRED_NOTIFICATION: Notification = {
  type: "ERROR",
  title: "Session expired",
  message: "Your session expired. Log in again to continue.",
  origin: "sessionExpired",
};

// Whitelist of authorized user emails for risk score approval
// These are the email addresses for Keli Munk, Morgan McDorman, and Nagasai Kotha
const AUTHORIZED_USER_EMAILS = [
  "keli_munk@avalonbay.com",
  "morgan_mcdorman@avalonbay.com",
  "nagasai_kotha.c@avalonbay.com",
  "evan_mcpheron.c@avalonbay.com",
  "michael_bloomberg@avalonbay.com",
  "michael_farnesi@avalonbay.com",
  "james_morris@avalonbay.com",
  "adhavann_ramalingam.c@avalonbay.com",
  "garapati_krishna@avalonbay.com",
];

/**
 * Check if current user is authorized to approve applications with risk scores
 * @returns {boolean} - True if user is authorized, false otherwise
 */
const isAuthorizedForRiskScoreApproval = (): boolean => {
  try {
    const token = fetchIdToken();
    if (!token) return false;

    // Remove "Bearer " prefix if present
    const cleanToken = token.replace(/^Bearer\s+/, "");
    const decodedToken = decodeToken(cleanToken);

    const userId = decodedToken["cognito:username"] || decodedToken.name;

    return (
      userId &&
      AUTHORIZED_USER_EMAILS.some((authorizedId) =>
        userId.toLowerCase().includes(authorizedId.toLowerCase())
      )
    );
  } catch (error) {
    console.error("Error checking approveWithRiskScore authorization:", error);
    return false;
  }
};

const getToken = async (user) => {
  const { apiKey, baseRoute } = getConfigs();

  const requestOptions = {
    method: "POST",
    headers: new Headers({
      "content-type": "application/json",
      "x-api-key": apiKey || "",
    }),
    body: JSON.stringify({
      emailAddress: user.email,
      password: user.password,
    }),
  };

  const url = `${baseRoute}/authenticate`;
  const resp = await fetch(url, requestOptions);
  return resp;
};

const getTokensFromAuthorizationCode = async (code) => {
  const { apiKey, baseRoute } = getConfigs();

  const requestOptions = {
    method: "POST",
    headers: new Headers({
      "content-type": "application/json",
      "x-api-key": apiKey || "",
    }),
    body: JSON.stringify({
      code: code,
    }),
  };

  const url = `${baseRoute}/credentials`;
  const resp = await fetch(url, requestOptions);
  return resp;
};

const refreshToken = async () => {
  const { apiKey, baseRoute } = getConfigs();

  const requestOptions = {
    method: "POST",
    headers: new Headers({
      "content-type": "application/json",
      "x-api-key": apiKey || "",
      Authorization: fetchIdToken() || "",
    }),
    body: JSON.stringify({
      refreshToken: fetchRefreshToken(),
    }),
  };

  const url = `${baseRoute}/refresh-token`;
  const resp = await fetch(url, requestOptions);
  return resp;
};

// Shared so parallel callers near expiry trigger a single /refresh-token request. Keyed by
// refresh token so a session that replaced the renewing one starts its own renewal.
let inFlightRenewal: {
  refreshToken: string | null;
  renewal: Promise<boolean>;
} | null = null;

/**
 * Exchanges the refresh token for new tokens and stores them.
 * Resolves false only when the current session's renewal is rejected and the associate must log in again.
 */
const renewSession = (): Promise<boolean> => {
  const renewingRefreshToken = fetchRefreshToken();
  if (inFlightRenewal?.refreshToken !== renewingRefreshToken) {
    // A logout or new login while the request is in flight makes its result stale: it is
    // neither stored nor reported as an expired session.
    const isSameSession = () => fetchRefreshToken() === renewingRefreshToken;
    const renewal: Promise<boolean> = refreshToken()
      .then(async (response) => {
        const credentials = response.ok ? await response.json() : null;
        if (!isSameSession()) return true;
        if (!credentials) {
          console.log("Token refresh rejected. Status:", response.status);
          return false;
        }
        storeCredentials(credentials);
        return true;
      })
      .catch((error) => {
        console.log("Error refreshing token...", error);
        return !isSameSession();
      })
      .finally(() => {
        if (inFlightRenewal?.renewal === renewal) inFlightRenewal = null;
      });
    inFlightRenewal = { refreshToken: renewingRefreshToken, renewal };
  }
  return inFlightRenewal.renewal;
};

const sessionNeedsRenewal = (): boolean => {
  const expiry = getIdTokenExpiry();
  if (expiry === null) return false;
  const { minTokenTimeLeft } = getConfigs();
  return expiry - Date.now() <= minTokenTimeLeft;
};

/**
 * Renews the session when the ID token expires within minTokenTimeLeft (or already has).
 * Resolves false only when a needed renewal was rejected.
 */
const ensureFreshSession = async (): Promise<boolean> =>
  sessionNeedsRenewal() ? renewSession() : true;

/**
 * fetch for AdminDash API calls. Waits for any needed session renewal, then swaps the tokens the
 * caller read into its headers for the renewed ones, so a request isn't sent with an expired token.
 */
export const fetchWithFreshSession = (
  input: RequestInfo,
  init: RequestInit = {}
): Promise<Response> =>
  sessionNeedsRenewal() ? fetchAfterRenewal(input, init) : fetch(input, init);

const fetchAfterRenewal = async (
  input: RequestInfo,
  init: RequestInit
): Promise<Response> => {
  const requestRefreshToken = fetchRefreshToken();
  const staleIdToken = fetchIdToken();
  const staleAccessToken = fetchAccessToken();
  await renewSession();
  // Never send a request started by one session with another session's tokens.
  if (fetchRefreshToken() !== requestRefreshToken) {
    throw new Error("Session changed while renewing tokens; request not sent");
  }
  const tokenSwaps = [
    [staleIdToken, fetchIdToken()],
    [staleAccessToken, fetchAccessToken()],
  ].filter(([stale, fresh]) => stale && fresh && stale !== fresh) as [
    string,
    string
  ][];
  if (tokenSwaps.length === 0) return fetch(input, init);

  const headers = new Headers(init.headers);
  const renewedHeaders: [string, string][] = [];
  headers.forEach((value, name) => {
    const renewed = tokenSwaps.reduce(
      (current, [stale, fresh]) => current.split(stale).join(fresh),
      value
    );
    if (renewed !== value) renewedHeaders.push([name, renewed]);
  });
  renewedHeaders.forEach(([name, value]) => headers.set(name, value));
  return fetch(input, { ...init, headers });
};

const AuthService = {
  getToken,
  getTokensFromAuthorizationCode,
  refreshToken,
  renewSession,
  ensureFreshSession,
  isAuthorizedForRiskScoreApproval,
};

export default AuthService;
